import { ReactNode, createContext, useCallback, useContext, useState } from 'react';
import ManagerApprovalModal from '../components/ManagerApprovalModal';

/**
 * useManagerApproval — imperative manager-PIN step-up.
 *
 * Usage in a component:
 *
 *   const askApproval = useManagerApproval();
 *
 *   async function applyBigDiscount() {
 *     const ok = await askApproval({
 *       action: 'Apply 30% discount',
 *       description: 'Cashier limit is 10%. Manager approval required.',
 *     });
 *     if (!ok) return;
 *     // The server now sees a valid hp_elevation cookie for ~30s.
 *     // Retry the privileged endpoint here.
 *     await fetch('/api/orders/lines/X/discount', { ... });
 *   }
 *
 * Cohesion: every privileged action funnels through this hook. No
 * other path opens the manager-PIN modal.
 *
 * App.tsx must wrap its tree with <ManagerApprovalProvider> for this
 * hook to work.
 */

interface AskInput {
  action: string;
  description?: string;
}

interface PendingRequest {
  input: AskInput;
  resolve: (ok: boolean) => void;
}

interface ContextValue {
  ask: (input: AskInput) => Promise<boolean>;
}

const ManagerApprovalContext = createContext<ContextValue | null>(null);

export function ManagerApprovalProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<PendingRequest | null>(null);

  const ask = useCallback((input: AskInput): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      setPending({ input, resolve });
    });
  }, []);

  const handleClose = useCallback(() => {
    if (pending) {
      pending.resolve(false);
      setPending(null);
    }
  }, [pending]);

  const handleApproved = useCallback(() => {
    if (pending) {
      pending.resolve(true);
      setPending(null);
    }
  }, [pending]);

  return (
    <ManagerApprovalContext.Provider value={{ ask }}>
      {children}
      <ManagerApprovalModal
        open={pending !== null}
        action={pending?.input.action ?? ''}
        description={pending?.input.description}
        onClose={handleClose}
        onApproved={handleApproved}
      />
    </ManagerApprovalContext.Provider>
  );
}

export function useManagerApproval() {
  const ctx = useContext(ManagerApprovalContext);
  if (!ctx) {
    throw new Error('useManagerApproval must be called inside <ManagerApprovalProvider>');
  }
  return ctx.ask;
}
